<template>
  <input
    :id="mfeNavToggleId"
    type="checkbox"
    class="hda-mfe-nav-toggle-input"
    autocomplete="off"
  />
  <svg xmlns="http://www.w3.org/2000/svg" class="clicky-menu-svg" hidden>
    <symbol id="clicky-menu-hamburger" viewBox="0 0 24 24">
      <line
        x1="3"
        y1="6"
        x2="21"
        y2="6"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
      <line
        x1="3"
        y1="12"
        x2="21"
        y2="12"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
      <line
        x1="3"
        y1="18"
        x2="21"
        y2="18"
        stroke="currentColor"
        stroke-width="2"
        stroke-linecap="round"
      />
    </symbol>
    <symbol id="clicky-menu-arrow" viewbox="0 0 16 16">
      <polyline
        points="4 6, 8 10, 12 6"
        stroke="currentColor"
        stroke-width="2"
        fill="none"
        stroke-linecap="round"
      />
    </symbol>
  </svg>
  <label
    class="hda-mfe-nav-toggle-label"
    :for="mfeNavToggleId"
    aria-label="Open menu"
  >
    <svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" width="24" height="24">
      <use xlink:href="#clicky-menu-hamburger" />
    </svg>
  </label>

  <nav :aria-label="ariaLabel">
    <clicky-menu
      ref="clickyHost"
      data-clicky-defer
      data-mfe-nav-sync
      :data-mfe-toggle-id="mfeNavToggleId"
    >
      <ul
        :id="id"
        class="clicky-menu no-js"
        :data-clicky-submenu-selector="submenuSelector"
      >
        <template v-for="navItem in navItems" :key="navItem.title">
          <li>
            <a :id="navItem.title" :href="navItem.href || '/'">
              {{ navItem.title }}
              <svg
                v-if="navItem.menuGroups"
                aria-hidden="true"
                width="16"
                height="16"
              >
                <use xlink:href="#clicky-menu-arrow" />
              </svg>
            </a>
            <template v-if="navItem.menuGroups">
              <ul>
                <template
                  v-for="(menuGroup, menuGroupIndex) in navItem.menuGroups"
                  :key="`menuGroup-${menuGroupIndex}`"
                >
                  <li v-if="menuGroupIndex > 0" class="clicky-menu-divider">
                    <hr />
                  </li>
                  <li
                    v-for="(listItem, listIndex) in menuGroup.listItems"
                    :key="`menuGroup-${menuGroupIndex}-listIndex-${listIndex}`"
                    v-bind="{
                      ...(listItem.key
                        ? { 'data-clicky-key': listItem.key }
                        : {}),
                      ...(listItem.parentKey
                        ? { 'data-clicky-parent-key': listItem.parentKey }
                        : {})
                    }"
                  >
                    <a :href="listItem.href">
                      {{ listItem.title }}
                      <svg
                        v-if="listItem.key"
                        aria-hidden="true"
                        width="16"
                        height="16"
                      >
                        <use xlink:href="#clicky-menu-arrow" />
                      </svg>
                    </a>
                  </li>
                </template>
              </ul>
            </template>
          </li>
        </template>
      </ul>
    </clicky-menu>
  </nav>
</template>

<script setup lang="ts">
import { nextTick, onMounted, useTemplateRef } from 'vue'
import type { ClickyMenuElement } from '#root/components/clicky-menu-element'
import type { NavItem } from '#root/types/nav'
import '#root/components/clicky-menu-element'
import '#root/styles/clicky-menu.css'

const clickyHost = useTemplateRef<ClickyMenuElement>('clickyHost')

/** `whenDefined` → `nextTick` so the menu tree matches Vue’s vdom before link→button enhancement. */
onMounted(async () => {
  await customElements.whenDefined('clicky-menu')
  await nextTick()
  clickyHost.value?.enhance()
})

withDefaults(
  defineProps<{
    id?: string
    ariaLabel?: string
    navItems?: NavItem[]
    submenuSelector?: string
    /** Mobile MFE shell checkbox id; must match `data-mfe-toggle-id` on `<clicky-menu>`. */
    mfeNavToggleId?: string
  }>(),
  {
    id: 'main-menu',
    ariaLabel: 'Main Navigation',
    navItems: () => [],
    submenuSelector: 'ul',
    mfeNavToggleId: 'hda-mfe-nav-toggle'
  }
)
</script>
